<template>
  <div>
    <div class="loading-container" v-if="articlesLoading">
      <loading-overlay></loading-overlay>
    </div>
    <transition name="fade">
      <div class="row py-5" v-if="!articlesLoading">
        <div
          class="col-12 background-articles"
          :class="noMatchesBackground"
          v-if="checkArticles"
        >
          <KnowledgeBlockArticle
            v-if="inputFilter.length === 0 && currentPage === paginationFirstPage"
            :title="'Most popular'"
            :show-reference-text="currentPage === 1 && checkHRPlatform"
            :custom-articles="slicedArticles"
          />
          <KnowledgeBlockArticle
            v-if="checkArticlesDisplaying"
            :title="displayedArticlesTitle"
            :show-reference-text="currentPage > 1 && checkHRPlatform"
            :custom-articles="displayedArticles"
          />
          <div
            v-if="this.inputFilter.length > 0 && filteredArticles.length === 0"
            class="no-matches-container row pl-4"
          >
            <h4>{{noMatchesFound}}</h4>
          </div>
          <div
            class="row ml-0 flex-md-row flex-column justify-content-center justify-content-md-between align-items-center"
            v-show="showPagination"
          >
            <span class="pagination-items-count mb-4 mb-md-0">{{ countItems }}</span>
            <pagination
              :per-page="perPage"
              :displayed-list="checkDisplayedList"
              @paginate="paginate"
            ></pagination>
          </div>
        </div>
      </div>
    </transition>
    <div v-if="checkEmptyArticles" class="mt-5 row justify-content-center align-items-center">
      <h5>{{ noArticlesFound }}</h5>
    </div>
  </div>
</template>
<script>
import KnowledgeBlockArticle from "@/components/knowledge-article/KnowledgeBlockArticle.vue";
import Pagination from "@/components/Pagination";
import LoadingOverlay from "@/components/LoadingOverlay";
import * as CONSTANTS from "@/store/constants";
import filter from "../../utils/filter";
import { mapGetters, mapMutations } from "vuex";

export default {
  name: "KnowledgeServices",
  data() {
    return {
      paginationFirstPage: CONSTANTS.PAGINATION_FIRST_PAGE,
      currentPage: 1,
      perPage: CONSTANTS.PAGINATION_PER_PAGE,
      noMatchesFound: "Sorry, no articles were found to match your keywords.",
      noArticlesFound: "Sorry, no articles were found for this category."
    };
  },
  props: {
    articles: Array
  },
  watch: {
    filteredArticles: function(newArticles, oldArticles) {
      if (newArticles !== oldArticles) {
        this.currentPage = 1;
      }
    },
    standardArticles: function(newArticles, oldArticles) {
      if (newArticles !== oldArticles) {
        this.currentPage = CONSTANTS.PAGINATION_FIRST_PAGE;
      }
    },
    inputFilter: function() {
      //Function to filter articles after every "inputFilter" change.
      //inputFilter is search input from KnowledgeCategories.
      const filteredArticles = this.standardArticles.filter(
        article =>
          article.name.toLowerCase().indexOf(this.inputFilter.toLowerCase()) !==
          -1
      );
      this.setFilteredArticles(filteredArticles);
    }
  },
  components: {
    KnowledgeBlockArticle,
    Pagination,
    LoadingOverlay
  },
  computed: {
    ...mapGetters([
      "articlesLoading",
      "errorMessage",
      "inputFilter",
      "filteredArticles",
      "standardArticles"
    ]),
    routeParams() {
      return this.$route.params;
    },
    showPagination: function() {
      const minLength = 18;
      return this.inputFilter.length === 0
        ? this.articles.length > minLength
        : this.filteredArticles.length > minLength;
    },
    slicedArticles: function() {
      const firstPosition = 0;
      const thirdPosition = 3;
      const sortedArticles = this.standardArticles.sort(
        (a, b) => b.viewCount - a.viewCount
      );
      return sortedArticles.slice(firstPosition, thirdPosition);
    },
    displayedArticlesTitle() {
      return this.inputFilter.length === 0 ? "All articles" : "";
    },
    checkArticlesDisplaying() {
      return this.inputFilter.length === 0
        ? this.commonArticles.length > 0
        : this.filteredArticles.length > 0;
    },
    commonArticles: function() {
      return this.standardArticles;
    },
    truncatedContent: function() {
      const ellipsis = "...";
      const truncateLength = 45;
      return content =>
        filter.hhTruncateHTML(content, truncateLength, ellipsis);
    },
    displayedArticles() {
      let sortedDisplayedArticles = [];
      let sortedArticles = [];
      let field='name';
      if (this.inputFilter.length < 1) {
        sortedArticles = this.commonArticles
          .slice()
          .filter(article => article.name && article.name.length > 0)
          .sort((a, b) => (a[field] || "").trim().toString().localeCompare((b[field] || "").trim().toString()));
        sortedDisplayedArticles = this.paginate(
          sortedArticles,
          this.currentPage
        );
      } else {
        sortedArticles = this.filteredArticles
        .filter(article => article.name && article.name.length > 0)
        .sort((a, b) => (a[field] || "").trim().toString().localeCompare((b[field] || "").trim().toString()));
        sortedDisplayedArticles = this.paginate(
          sortedArticles,
          this.currentPage
        );
      }
      return sortedDisplayedArticles;
    },

    checkArticles() {
      return this.standardArticles.length && !this.articlesLoading;
    },
    checkEmptyArticles() {
      return this.standardArticles.length === 0 && !this.articlesLoading;
    },
    countItems() {
      const currentItems = this.perPage * this.currentPage;

      let articlesLength =
        this.inputFilter.length === 0
          ? this.commonArticles.length
          : this.filteredArticles.length;

      let paginationTextPrefix = `${currentItems}`;
      let prevOffset = currentItems - this.perPage + 1;
      paginationTextPrefix = `${prevOffset} - ${
        currentItems > articlesLength ? articlesLength : currentItems
      }`;
      return `${paginationTextPrefix} of ${articlesLength} articles`;
    },
    checkDisplayedList() {
      return this.inputFilter.length === 0
        ? this.commonArticles
        : this.filteredArticles;
    },
    noMatchesBackground() {
      return this.inputFilter.length > 0 && this.filteredArticles.length === 0
        ? "no-matches-background py-3"
        : "py-4";
    },
    checkHRPlatform() {
      return this.routeParams.platformCategory === CONSTANTS.HR_PLATFORM;
    }
  },
  methods: {
    ...mapMutations(["setFilteredArticles", "setArticlesLoading"]),
    paginate(posts, page) {
      this.currentPage = page;
      let perPage = this.perPage;
      let from = page * perPage - perPage;
      let to = page * perPage;
      return posts.slice(from, to);
    }
  }
};
</script>

<style scoped>
.helphub-app-container {
  min-height: 100vh;
}

.eh-services-index {
  font-size: 14px;
  color: #64788d;
}
.background-articles {
  background-color: #fff;
}

.no-matches-background {
  background-color: #f4f8fa;
  color: #979797;
}

.background-articles h5 {
  font-size: 18px;
  line-height: 25px;
  color: #2f3642;
  font-weight: 600;
}
.article-title-container a {
  font-size: 14px;
  color: #0696d7;
  font-weight: bold;
  line-height: 20px;
}

.article-description-container p {
  font-size: 16px;
  line-height: 20px;
  color: #000000 !important;
}

.pagination-items-count {
  color: #000000;
  font-size: 12px;
  font-weight: bold;
}
.loading-container {
  min-height: 85vh;
}

.no-matches-container h4 {
  margin-bottom: 0;
}
</style>
<style>
.eh-service-item {
  max-width: none !important;
}
</style>


// WEBPACK FOOTER //
// src/components/knowledge-article/KnowledgeServices.vue